import { reorder, toArchive } from '../actions';
import { CreateDTO, KBEntity, Statuses, UpdateDTO } from '../admin-knowledge-base.type';
import { useCallback, useEffect, useState } from 'react';
import { isArticle, isTag } from '../admin-knowledge-base.utils';
import { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';

interface Options<Entity extends KBEntity> {
    getAction: (parentKey?: string) => Promise<Entity[]>;
    createAction: (dto: CreateDTO<Entity>) => Promise<Entity>;
    updateAction: (dto: UpdateDTO<Entity>) => Promise<Entity>;
}

export const useKbEntity = <Entity extends KBEntity>({
    getAction,
    createAction,
    updateAction,
}: Options<Entity>) => {
    const [items, setItems] = useState<Entity[]>([]);
    const [active, setActive] = useState<Entity>(null);
    const [isLoading, setIsLoading] = useState(false);

    const fetchItems = useCallback(async (parent?: KBEntity) => {
        setIsLoading(true);

        const actionFn = parent ? () => getAction(parent.path) : getAction;

        const data = await actionFn();
        setItems(data);

        const activeCandidate = data[0];
        const hasActive =
            activeCandidate && !(isArticle(activeCandidate) || isTag(activeCandidate));
        setActive(hasActive ? activeCandidate : null);

        setIsLoading(false);
    }, []);

    // Check if active item has been deleted (put to archive). In this case first item in the list becomes active
    useEffect(() => {
        if (active && !items.includes(active)) setActive(items[0] ?? null);
    }, [items]);

    const create = useCallback(async (dto: CreateDTO<Entity>) => {
        try {
            setIsLoading(true);

            const created = await createAction(dto);

            setItems((prev) => [...prev, created]);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const update = useCallback(async (dto: UpdateDTO<Entity>) => {
        try {
            setIsLoading(true);

            const updated = await updateAction(dto);

            setItems((prev) =>
                prev.reduce((acc, entity) => {
                    if (entity.id !== updated.id) {
                        acc.push(entity);
                    } else {
                        if (entity.parent === updated.parent) {
                            acc.push(updated);
                        }
                    }
                    return acc;
                }, new Array<Entity>())
            );
        } finally {
            setIsLoading(false);
        }
    }, []);

    const setActiveByEntity = useCallback(
        (entity?: Entity) => setActive(entity && items.find((item) => item.id === entity.id)),
        [items]
    );

    const handleReorder = useCallback((event: DragEndEvent) => {
        const { active, over } = event;

        if (active.id !== over.id) {
            setItems((items) => {
                const oldIndex = items.findIndex((item) => item.id === active.id);
                const newIndex = items.findIndex((item) => item.id === over.id);

                let reordered = arrayMove(items, oldIndex, newIndex);

                reorder({ order: reordered.map((entity) => entity.id) }).catch(
                    () => setItems(items) // Revert to initial values in case of error
                );
                return reordered;
            });
        }
    }, []);

    const archive = useCallback(async (id: string, effective?: Date) => {
        try {
            setIsLoading(true);
            const updated = await toArchive(id, effective);
            setItems((prev) =>
                prev.filter(
                    (item) => item.id !== updated.id || updated.status.current !== Statuses.ARCHIVE
                )
            );
        } finally {
            setIsLoading(false);
        }
    }, []);

    return {
        items,
        active,
        setActive: setActiveByEntity,
        isLoading,
        create,
        update,
        archive,
        fetchItems,
        handleReorder,
    };
};
